<script setup lang="ts">
import { Planungsgeschaeft } from '@/models/Planungsgeschaeft';
import { inject, type Ref } from 'vue';
import PrevNextBtnBar from '@/components/PrevNextBtnBar.vue';
import FileUpload from '@/components/FileUpload.vue';
import { useDocumentConfig } from '@/models/Document';
import { hasPpaReachedSubmitRoute } from '@/helpers/tools';

// injected from PlanungsgeschaeftEditView
const saving = inject('saving') as Ref<boolean>;
const readonly = inject('readonly') as Ref<boolean>;

const documentConfig = useDocumentConfig();

const props = defineProps<{
    geschaeft: Planungsgeschaeft;
}>();

const forceValidation = hasPpaReachedSubmitRoute(props.geschaeft);

const emit = defineEmits<{
    next: [];
    prev: [];
}>();

function onReset() {
    emit('prev');
}

async function onNextStep() {
    emit('next');
}
</script>

<template>
    <div>
        <h4 class="ktg-title-04 ktg-inner-box-padding mb-1_5rem">{{ $t('berichte_plaene_vorschriften') }}</h4>
        <fieldset class="ktg-fieldset ktg-form-area-maxwidth py-3_5rem px-6rem">
            <h5 class="ktg-title-05 ktg-inner-box-padding">{{ $t('berichte_plaene_vorschriften') }}</h5>
            <ktg-line class="mt-0_5rem mb-1rem"></ktg-line>

            <FileUpload
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="rechtsverbindliche_plaene"
                :title="
                    $t('rechtsverbindliche_plaene') +
                    (documentConfig?.planungsgeschaeft?.rechtsverbindliche_plaene?.min > 0 ? ' *' : '')
                "
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.rechtsverbindliche_plaene?.min"
                :max="documentConfig?.planungsgeschaeft?.rechtsverbindliche_plaene?.max"
                :display-min="documentConfig?.planungsgeschaeft?.rechtsverbindliche_plaene?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_rechtsverbindliche_plaene')"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="ergaenzende_plaene"
                :title="
                    $t('ergaenzende_plaene') +
                    (documentConfig?.planungsgeschaeft?.ergaenzende_plaene?.min > 0 ? ' *' : '')
                "
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.ergaenzende_plaene?.min"
                :max="documentConfig?.planungsgeschaeft?.ergaenzende_plaene?.max"
                :display-min="documentConfig?.planungsgeschaeft?.ergaenzende_plaene?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_ergaenzende_plaene')"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="ergaenzende_berichte"
                :title="
                    $t('ergaenzende_berichte') +
                    (documentConfig?.planungsgeschaeft?.ergaenzende_berichte?.min > 0 ? ' *' : '')
                "
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.ergaenzende_berichte?.min"
                :max="documentConfig?.planungsgeschaeft?.ergaenzende_berichte?.max"
                :display-min="documentConfig?.planungsgeschaeft?.ergaenzende_berichte?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_ergaenzende_berichte')"
            ></FileUpload>

            <FileUpload
                class="mt-1rem"
                ref="fileupload"
                :parent-id="geschaeft.id || 0"
                context="vorschriften"
                :title="$t('vorschriften') + (documentConfig?.planungsgeschaeft?.vorschriften?.min > 0 ? ' *' : '')"
                entity="planungsgeschaeft"
                :min="!forceValidation ? 0 : documentConfig?.planungsgeschaeft?.vorschriften?.min"
                :max="documentConfig?.planungsgeschaeft?.vorschriften?.max"
                :display-min="documentConfig?.planungsgeschaeft?.vorschriften?.min"
                :readonly="readonly"
                :show-deleted-switch="geschaeft.aktueller_status_ebau_value !== 'new'"
                @upload-in-progress="saving = $event"
                :tooltip="$t('file_infotext_ergaenzende_vorschriften')"
            ></FileUpload>

            <ktg-line class="mt-2rem mb-1rem"></ktg-line>
            <PrevNextBtnBar
                :back-text="$t('back')"
                :next-text="$t('to_next_step')"
                :disabled="saving"
                @next="onNextStep"
                @back="onReset"
            />
        </fieldset>
    </div>
</template>
